import { AdviseEngagement } from '../../../types/advise';
import {
  updateAdviseEngagement,
  updateAdviseScores,
  updateAdviseRoi,
  updateAdviseActionPlan
} from '../../../Api/Advise/Engagements';
import { updateAdviseLineItems } from '../../../Api/Advise/Quotes';

type EngagementField = keyof AdviseEngagement;

export interface EngagementSave {
  key: string;
  save: (engagement: AdviseEngagement) => Promise<{ error: { message: string } | null }>;
}

// Fields an endpoint writes together share a key, so a pending save always sends every one of them as edited last.
const GROUPED_SAVES: (EngagementSave & { fields: EngagementField[] })[] = [
  {
    key: 'scores',
    fields: ['supplier_scores'],
    save: (e) => updateAdviseScores(e.id, { supplier_scores: e.supplier_scores || {} })
  },
  {
    key: 'lineItems',
    fields: ['line_item_mapping', 'telephony_usage_overrides'],
    save: (e) => updateAdviseLineItems(e.id, e.line_item_mapping || {}, e.telephony_usage_overrides)
  },
  { key: 'roi', fields: ['roi_analysis'], save: (e) => updateAdviseRoi(e.id, e.roi_analysis) },
  {
    key: 'actionPlan',
    fields: ['mutual_action_plan'],
    save: (e) => updateAdviseActionPlan(e.id, { milestones: e.mutual_action_plan?.milestones ?? [] })
  }
];

/** The saves that write a patch's fields, one per endpoint; a field no endpoint groups is sent alone to the engagement. */
export const savesForPatch = (patch: Partial<AdviseEngagement>): EngagementSave[] => {
  const saves = new Map<string, EngagementSave>();
  (Object.keys(patch) as EngagementField[]).forEach((field) => {
    const grouped = GROUPED_SAVES.find((group) => group.fields.includes(field));
    if (grouped) saves.set(grouped.key, grouped);
    else saves.set(field, { key: field, save: (e) => updateAdviseEngagement(e.id, { [field]: e[field] }) });
  });
  return [...saves.values()];
};
